/**
 * Reseller Settlements tab (TMMPDM-0037, tmm-api 0099) — replaces the Payouts tab. One settlement
 * per calendar month (UTC) per currency, newest first: Month · Currency · Margin · Affiliates ·
 * Net · Outcome. Every amount is in its own row's currency and nothing is summed across rows
 * (DATA-003). The outcome is read from `payoutStatus` alone: Paid {date} · To pay · Carried (under
 * minimum) · Charged to card, with the charge's status and a link to it on the Billing tab;
 * RETAINED is an explained chip and never a paid amount.
 * Expanding a row shows `byLine[]` (AI at our price in micro, never provider cost) and the
 * statement's `lines[]`. A `PENDING` row keeps Record payout… (the payout-tenant route); a
 * `CHARGED` or `RETAINED` one has no action. Filters (`currency`, `from`, `to`, `status`) live in
 * the URL beside `?tab=`, so a deep link or Back restores them. Status filters the complete
 * returned list locally; the pinned endpoint accepts only currency/from/to.
 * TMMPDM-0095 / 0096: when the old payout actions are retired the tab is frozen history: a banner
 * links the partner's Ledger and Record payout… is gone. Before that, nothing changes.
 */
import { Fragment, useEffect, useState } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  Alert,
  Box,
  Collapse,
  FormControl,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Tooltip,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCopy } from '@/hooks/useCopy';
import { isoDate, oneOf, useUrlState, type UrlSchema } from '@/hooks/useUrlState';
import { EmptyState, ErrorState, useNotify } from '@/components/page';
import { Amount } from '@/components/ui';
import { formatDate, formatMoney, formatMonthYear } from '@/format';
import { adminApi } from '@/api/tmm/admin';
import { PAYOUT_STATUSES } from '@/api/tmm/types';
import type {
  PayoutHold,
  PayoutStatementView,
  RevenueLineKind,
  SettlementLineSummary,
} from '@/api/tmm/types';
import { RecordPayoutAction, RecordPayoutDialog } from '../payouts/RecordPayoutDialog';
import { FrozenHistoryBanner } from '../ledger/ledgerShared';
import { resellerLedgerPath, usePayoutActionsRetired } from '../ledger/ledger';
import { StatementLinesTable } from '../payouts/StatementLinesTable';
import { ResellerChargeChip } from './ResellerChargeChip';
import { SETTLEMENT_OUTCOME_FALLBACK, chargeHref } from './settlementFormat';
import { StatusChip } from '@/status';

interface ResellerSettlementsPanelProps {
  resellerId: number;
}

type Filters = { currency: string; from: string; to: string; status: string };

const VIEW: UrlSchema<Filters> = {
  status: { default: '', parse: oneOf(['', ...PAYOUT_STATUSES]) },
  currency: { default: '', parse: (raw) => (/^[A-Z]{3}$/.test(raw) ? raw : undefined) },
  from: { default: '', parse: isoDate },
  to: { default: '', parse: isoDate },
};

const NO_FILTERS: Filters = { currency: '', from: '', to: '', status: '' };

const HOLD_FALLBACK: Record<PayoutHold, string> = {
  HOLD_TAX_DOCS: 'Tax docs',
  HOLD_STRIPE_CONNECT: 'Stripe Connect',
  HOLD_TRANSFER_CURRENCY: 'Transfer currency',
  HOLD_TRANSFER_CAP: 'Transfer cap',
  HOLD_RESELLER_UNPAID: 'Reseller charge unpaid',
  HOLD_AFFILIATE_REVIEW: 'Held for review (affiliate ended for cause)',
};

const LINE_FALLBACK: Record<RevenueLineKind, string> = {
  BASE: 'Subscriptions',
  SEAT: 'Guest seats',
  CONNECTED: 'Connected accounts',
  AI: 'AI',
  TAX: 'Tax',
};

const COLUMNS = 8;

export function ResellerSettlementsPanel({ resellerId }: ResellerSettlementsPanelProps) {
  const { t } = useCopy();
  const notify = useNotify();
  const { i18n } = useTranslation();
  const locale = i18n.language;
  const [filters, setFilters] = useUrlState(VIEW);
  const [reloadToken, setReloadToken] = useState(0);
  const key = `${resellerId}:${filters.currency}:${filters.from}:${filters.to}:${reloadToken}`;
  const [state, setState] = useState<{
    key: string;
    rows: PayoutStatementView[] | null;
    failure: unknown;
  }>({ key: '', rows: null, failure: null });
  // Every currency a load has shown, so the filter can offer them after narrowing to one.
  const [currencies, setCurrencies] = useState<string[]>([]);
  const [expanded, setExpanded] = useState<number | null>(null);
  const [recording, setRecording] = useState<PayoutStatementView | null>(null);
  // TMMPDM-0044: a payout the server refused — said here once the dialog closes and the list re-reads.
  const [refusal, setRefusal] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    adminApi
      .listResellerSettlements(resellerId, filters)
      .then((list) => {
        if (cancelled) return;
        setState({ key, rows: list.payoutStatements, failure: null });
        setCurrencies((known) => {
          const next = new Set(known);
          for (const row of list.payoutStatements) next.add(row.currency);
          return next.size === known.length ? known : [...next].sort();
        });
      })
      .catch((failure: unknown) => {
        if (!cancelled) setState({ key, rows: null, failure });
      });
    return () => {
      cancelled = true;
    };
  }, [key, resellerId, filters]);

  const current = state.key === key;
  const rows = current
    ? (state.rows?.filter((row) => !filters.status || row.payoutStatus === filters.status) ?? null)
    : null;
  const failure = current ? state.failure : null;
  const filtered =
    filters.status !== '' || filters.currency !== '' || filters.from !== '' || filters.to !== '';
  const reload = () => setReloadToken((n) => n + 1);
  const payoutActions = usePayoutActionsRetired();
  const frozen = payoutActions.retired;
  // TMMPDM-0044: a well-formed currency from the URL is offered even before a load shows it.
  const currencyOptions =
    filters.currency !== '' && !currencies.includes(filters.currency)
      ? [...currencies, filters.currency]
      : currencies;
  const setFilter = (name: keyof Filters, value: string) => setFilters({ [name]: value });
  // TMMPDM-0044: a date field commits as it is typed, so it replaces — no Back step per keystroke.
  const setDate = (name: 'from' | 'to', value: string) =>
    setFilters({ [name]: isoDate(value) ?? '' }, { history: 'replace' });

  return (
    <Box>
      {frozen ? <FrozenHistoryBanner ledgerTo={resellerLedgerPath(resellerId)} /> : null}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        useFlexGap
        sx={{ mb: 2, flexWrap: 'wrap', alignItems: { sm: 'center' } }}
      >
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="settlements-currency-label">
            {t('resellers.settlements.filter.currency', 'Currency')}
          </InputLabel>
          <Select
            labelId="settlements-currency-label"
            value={filters.currency}
            label={t('resellers.settlements.filter.currency', 'Currency')}
            onChange={(event) => setFilter('currency', event.target.value)}
          >
            <MenuItem value="">{t('resellers.settlements.filter.allCurrencies', 'All')}</MenuItem>
            {currencyOptions.map((currency) => (
              <MenuItem key={currency} value={currency}>
                {currency}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="settlements-status-label">
            {t('resellers.settlements.filter.status', 'Status')}
          </InputLabel>
          <Select
            labelId="settlements-status-label"
            value={filters.status}
            label={t('resellers.settlements.filter.status', 'Status')}
            onChange={(event) => setFilter('status', event.target.value)}
          >
            <MenuItem value="">{t('resellers.settlements.filter.allStatuses', 'All')}</MenuItem>
            {PAYOUT_STATUSES.map((status) => (
              <MenuItem key={status} value={status}>
                {t(`payouts.status.${status}`, status)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          size="small"
          type="date"
          label={t('resellers.settlements.filter.from', 'From')}
          value={filters.from}
          onChange={(event) => setDate('from', event.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <TextField
          size="small"
          type="date"
          label={t('resellers.settlements.filter.to', 'To')}
          value={filters.to}
          onChange={(event) => setDate('to', event.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
        />
      </Stack>

      {refusal ? (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setRefusal(null)}>
          {refusal}
        </Alert>
      ) : null}
      {failure !== null ? (
        <Box sx={{ mb: 2 }}>
          <ErrorState
            error={failure}
            message={t('resellers.settlements.loadError', 'Could not load settlements.')}
            onRetry={reload}
          />
        </Box>
      ) : null}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small" aria-label={t('resellers.settlements.tableAria', 'Settlements')}>
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>{t('resellers.settlements.col.month', 'Month')}</TableCell>
              <TableCell>{t('resellers.settlements.col.currency', 'Currency')}</TableCell>
              <TableCell align="right">{t('resellers.settlements.col.margin', 'Margin')}</TableCell>
              <TableCell align="right">
                {t('resellers.settlements.col.affiliates', 'Affiliates')}
              </TableCell>
              <TableCell align="right">{t('resellers.settlements.col.net', 'Net')}</TableCell>
              <TableCell>{t('resellers.settlements.col.outcome', 'Outcome')}</TableCell>
              <TableCell>{t('resellers.settlements.col.actions', 'Actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows === null ? (
              <TableRow>
                <TableCell colSpan={COLUMNS} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    {failure === null ? t('common.loading', 'Loading') : null}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={COLUMNS} align="center" sx={{ py: 2 }}>
                  <EmptyState
                    title={t('resellers.settlements.empty', 'No settlements.')}
                    {...(filtered
                      ? { filtered: { onClear: () => setFilters(NO_FILTERS) } }
                      : {
                          body: t(
                            'resellers.settlements.emptyBody',
                            'Each month closes on the 2nd of the next month, one settlement per currency.',
                          ),
                        })}
                  />
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => {
                const open = expanded === row.id;
                const month = formatMonthYear(row.periodStart, locale);
                const hasAffiliateEvents =
                  row.affiliateBountiesMinor !== undefined ||
                  row.affiliateMilestonesMinor !== undefined ||
                  row.affiliateRecoveredMinor !== undefined;
                const hasDetail =
                  row.byLine !== undefined || row.lines !== undefined || hasAffiliateEvents;
                return (
                  <Fragment key={row.id}>
                    <TableRow hover>
                      <TableCell>
                        {hasDetail ? (
                          <IconButton
                            size="small"
                            aria-expanded={open}
                            aria-label={
                              open
                                ? t('resellers.settlements.collapse', 'Collapse {{month}}', {
                                    month,
                                  })
                                : t('resellers.settlements.expand', 'Expand {{month}}', { month })
                            }
                            onClick={() => setExpanded(open ? null : row.id)}
                          >
                            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                          </IconButton>
                        ) : null}
                      </TableCell>
                      <TableCell>{month}</TableCell>
                      <TableCell>{row.currency}</TableCell>
                      <TableCell align="right">
                        <Amount
                          minor={row.resellerMarginMinor}
                          currency={row.currency}
                          locale={locale}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Amount
                          minor={row.affiliateCommissionMinor}
                          currency={row.currency}
                          locale={locale}
                        />
                      </TableCell>
                      <TableCell align="right">
                        {row.retainedPart || row.normalPart ? (
                          '—'
                        ) : (
                          <Amount
                            minor={row.netMinor}
                            currency={row.currency}
                            locale={locale}
                            emphasis
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Stack spacing={0.5}>
                          <SettlementOutcome row={row} locale={locale} month={month} />
                          {row.note ? <Typography variant="body2">{row.note}</Typography> : null}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        {!frozen && row.payoutStatus === 'PENDING' ? (
                          <RecordPayoutAction
                            row={row}
                            onRecord={setRecording}
                            ariaLabel={t(
                              'resellers.settlements.recordAria',
                              'Record payout… for {{month}} {{currency}}',
                              { month, currency: row.currency },
                            )}
                          />
                        ) : null}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        colSpan={COLUMNS}
                        sx={{ py: 0, borderBottom: open ? undefined : 'none' }}
                      >
                        <Collapse in={open} timeout="auto" unmountOnExit>
                          <Stack spacing={2} sx={{ py: 1 }}>
                            {hasAffiliateEvents ? (
                              <AffiliateEventLines row={row} locale={locale} />
                            ) : null}
                            {row.byLine !== undefined ? (
                              <ByLineTable
                                rows={row.byLine}
                                currency={row.currency}
                                locale={locale}
                              />
                            ) : null}
                            {row.lines !== undefined ? (
                              <StatementLinesTable
                                lines={row.lines}
                                locale={locale}
                                revenueShare={false}
                              />
                            ) : null}
                          </Stack>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <RecordPayoutDialog
        open={recording !== null}
        row={recording}
        onClose={() => setRecording(null)}
        onRecorded={() => {
          setRecording(null);
          setRefusal(null);
          notify.success(t('payouts.record.recorded', 'Payout recorded.'));
          reload();
        }}
        onRefused={(message) => {
          setRecording(null);
          setRefusal(message);
          reload();
        }}
        onMovedToLedger={() => {
          payoutActions.reload();
          reload();
        }}
      />
    </Box>
  );
}

function SettlementOutcome({
  row,
  locale,
  month,
}: {
  row: PayoutStatementView;
  locale: string;
  month: string;
}) {
  const { t } = useCopy();
  const status = row.payoutStatus;
  const known = Object.hasOwn(SETTLEMENT_OUTCOME_FALLBACK, status);
  const words = known
    ? t(`resellers.settlements.outcome.${status}`, SETTLEMENT_OUTCOME_FALLBACK[status], {
        date: formatDate(row.payoutAt, locale),
      })
    : status;
  return (
    <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
      {status === 'RETAINED' ? (
        <Tooltip
          title={t(
            'resellers.settlements.retainedTooltip',
            'Kept by the platform while it manages this partner; nothing is paid out.',
          )}
        >
          <StatusChip status="RETAINED" label={words} tabIndex={0} />
        </Tooltip>
      ) : (
        <Typography variant="body2" component="span">
          {words}
        </Typography>
      )}
      {status === 'CHARGED' && row.charge !== undefined ? (
        <>
          <ResellerChargeChip status={row.charge.status} />
          <Link
            component={RouterLink}
            to={chargeHref(row.charge.id)}
            underline="always"
            variant="body2"
            aria-label={t(
              'resellers.settlements.viewChargeAria',
              'View charge for {{month}} {{currency}}',
              { month, currency: row.currency },
            )}
          >
            {t('resellers.standing.viewCharge', 'View charge')}
          </Link>
        </>
      ) : null}
      {row.hold !== undefined ? (
        <StatusChip tone="warning" label={t(`payouts.hold.${row.hold}`, HOLD_FALLBACK[row.hold])} />
      ) : null}
      {row.retainedPart ? (
        <Typography variant="body2" component="span">
          {t('resellers.settlements.retainedPart', 'Retained {{amount}}', {
            amount: formatMoney(row.retainedPart.netMinor, row.currency, locale),
          })}
        </Typography>
      ) : null}
      {row.normalPart ? (
        <Typography variant="body2" component="span">
          {t('resellers.settlements.normalPart', 'Payable {{amount}}', {
            amount: formatMoney(row.normalPart.payableMinor, row.currency, locale),
          })}
        </Typography>
      ) : null}
    </Stack>
  );
}

/**
 * Bounty and milestone debits and the recovery credit. The server sends magnitudes.
 * The sign is the line's kind: bounties and milestones are negative, recovered clawbacks positive.
 */
function EventAmount({
  credit,
  minor,
  currency,
  locale,
}: {
  credit: boolean;
  minor: number;
  currency: string;
  locale: string;
}) {
  // Credits and debits are listed together here, so a credit carries an explicit "+" (TMM-B1).
  const magnitude = Math.abs(minor);
  return (
    <Amount minor={credit ? magnitude : -magnitude} currency={currency} locale={locale} signed />
  );
}

function AffiliateEventLines({ row, locale }: { row: PayoutStatementView; locale: string }) {
  const { t } = useCopy();
  const lines: { key: string; label: string; minor: number }[] = [];
  if (row.affiliateBountiesMinor !== undefined) {
    lines.push({
      key: 'bounties',
      label: t('resellers.settlements.events.bounties', 'Affiliate bounties'),
      minor: row.affiliateBountiesMinor,
    });
  }
  if (row.affiliateMilestonesMinor !== undefined) {
    lines.push({
      key: 'milestones',
      label: t('resellers.settlements.events.milestones', 'Affiliate milestones'),
      minor: row.affiliateMilestonesMinor,
    });
  }
  if (row.affiliateRecoveredMinor !== undefined) {
    lines.push({
      key: 'recovered',
      label: t('resellers.settlements.events.recovered', 'Recovered clawbacks'),
      minor: row.affiliateRecoveredMinor,
    });
  }
  return (
    <Table
      size="small"
      aria-label={t('resellers.settlements.events.aria', 'Affiliate bounties and milestones')}
    >
      <TableHead>
        <TableRow>
          <TableCell>{t('resellers.settlements.events.line', 'Line')}</TableCell>
          <TableCell align="right">{t('resellers.settlements.events.amount', 'Amount')}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {lines.map((line) => (
          <TableRow key={line.key}>
            <TableCell>{line.label}</TableCell>
            <TableCell align="right">
              <EventAmount
                credit={line.key === 'recovered'}
                minor={line.minor}
                currency={row.currency}
                locale={locale}
              />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function ByLineTable({
  rows,
  currency,
  locale,
}: {
  rows: SettlementLineSummary[];
  currency: string;
  locale: string;
}) {
  const { t } = useCopy();
  return (
    <Table size="small" aria-label={t('resellers.settlements.byLineAria', 'Settlement by line')}>
      <TableHead>
        <TableRow>
          <TableCell>{t('resellers.settlements.byLine.line', 'Line')}</TableCell>
          <TableCell align="right">{t('resellers.settlements.byLine.retail', 'Retail')}</TableCell>
          <TableCell align="right">
            {t('resellers.settlements.byLine.wholesale', 'Wholesale')}
          </TableCell>
          <TableCell align="right">{t('resellers.settlements.byLine.margin', 'Margin')}</TableCell>
          <TableCell align="right">
            {t('resellers.settlements.byLine.affiliates', 'Affiliates')}
          </TableCell>
          <TableCell align="right">
            {t('resellers.settlements.byLine.aiAtOurPrice', 'AI at our price')}
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.line}>
            <TableCell>{t(`revenue.line.${row.line}`, LINE_FALLBACK[row.line])}</TableCell>
            <TableCell align="right">
              <Amount minor={row.retailMinor} currency={currency} locale={locale} />
            </TableCell>
            <TableCell align="right">
              <Amount minor={row.wholesaleMinor} currency={currency} locale={locale} />
            </TableCell>
            <TableCell align="right">
              <Amount minor={row.resellerMarginMinor} currency={currency} locale={locale} />
            </TableCell>
            <TableCell align="right">
              <Amount minor={row.affiliateCommissionMinor} currency={currency} locale={locale} />
            </TableCell>
            <TableCell align="right">
              <Amount micro={row.aiAtOurPriceMicro} currency={currency} locale={locale} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
